import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useSimulation } from '../../contexts/SimulationContext';
import CONFIG from '../../config';
import CandidateDetailDrawer from './CandidateDetailDrawer';
import LoadingSpinner from '../common/LoadingSpinner';

const Analytics = () => {
  const { project, candidates, alerts, loading: contextLoading } = useSimulation();
  const [searchTerm, setSearchTerm] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Candidate Journey Search Logic
  const foundCandidate = searchTerm 
    ? candidates.find(c => 
        c.unique_id?.toLowerCase() === searchTerm.toLowerCase() || 
        c.chest_number?.toLowerCase() === searchTerm.toLowerCase()
      ) 
    : null;

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${CONFIG.API_URL}/api/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          user: userSearch,
          role: roleFilter,
          type: typeFilter,
          projectId: project?._id || 'all',
          limit: 10
        }
      });
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotalLogs(response.data.total);
    } catch (err) {
      console.error('Fetch logs failed:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, userSearch, roleFilter, typeFilter, project?._id]);

  const roles = [
    'ADMIN', 'ENTRY_STAFF', 'RUN_OFFICER', 'FIELD_MONITOR',
    'STAGE_EVALUATOR', 'DOCTOR', 'SYSTEM'
  ];

  // Calculate Stage Performance from real candidate data
  const getStageStats = () => {
    if (!project || !project.stages) return [];
    
    return project.stages.map(s => {
      const stageName = s.name.toUpperCase();
      const results = candidates.flatMap(c => c.test_results || [])
        .filter(r => r.stage_name === stageName);
      
      const total = results.length;
      if (total === 0) return { name: s.name, pass: 0, fail: 0, total: 0 };
      
      const passCount = results.filter(r => r.result === 'PASS').length;
      const failCount = total - passCount;
      
      return {
        name: s.name,
        pass: Math.round((passCount / total) * 100),
        fail: Math.round((failCount / total) * 100),
        total
      };
    });
  };

  const performanceStats = getStageStats();

  // --- EXPORT CSV ---
  const exportCSV = () => {
    const now = new Date();
    const projectName = project?.name || 'All Projects';
    const totalCandidates = candidates.length;
    const rejectedCount = candidates.filter(c => c.status === 'REJECTED').length;
    const completedCount = candidates.filter(c => c.status === 'COMPLETED').length;
    const activeCount = totalCandidates - rejectedCount - completedCount;
    let totalTests = 0, passedTests = 0;
    candidates.forEach(c => { (c.test_results || []).forEach(r => { totalTests++; if (r.result === 'PASS') passedTests++; }); });
    const passRate = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;

    const sep = () => ['='.repeat(80)];
    const blank = () => [];
    const sectionHeader = (title) => [`>>> ${title.toUpperCase()} <<<`];

    const rows = [];

    // ── COVER ──
    rows.push(['ICFF ADMIN SYSTEM — PROJECT ANALYTICS REPORT']);
    rows.push(sep());
    rows.push([`Project Name`, projectName]);
    rows.push([`Report Generated`, now.toLocaleString()]);
    rows.push([`Generated By`, localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).full_name || 'Admin' : 'Admin']);
    rows.push(sep());
    rows.push(blank());

    // ── EXECUTIVE SUMMARY ──
    rows.push(sectionHeader('Executive Summary'));
    rows.push(['Metric', 'Value']);
    rows.push(['Total Registered Candidates', totalCandidates]);
    rows.push(['Active in Pipeline', activeCount]);
    rows.push(['Eliminated / Rejected', rejectedCount]);
    rows.push(['Successfully Completed', completedCount]);
    rows.push(['Total Tests Conducted', totalTests]);
    rows.push(['Tests Passed', passedTests]);
    rows.push(['Tests Failed', totalTests - passedTests]);
    rows.push(['Overall Pass Rate', `${passRate}%`]);
    rows.push(['Elimination Rate', totalTests > 0 ? `${100 - passRate}%` : '0%']);
    rows.push(blank());

    // ── STAGE PERFORMANCE ──
    rows.push(sectionHeader('Stage-Wise Performance'));
    rows.push(['Stage Name', 'Tests Conducted', 'Passed', 'Failed', 'Pass Rate (%)', 'Fail Rate (%)']);
    if (performanceStats.length === 0) {
      rows.push(['No stage data available', '', '', '', '', '']);
    } else {
      performanceStats.forEach(s => {
        const passed = Math.round((s.pass / 100) * s.total);
        const failed = s.total - passed;
        rows.push([s.name, s.total, passed, failed, `${s.pass}%`, `${s.fail}%`]);
      });
    }
    rows.push(blank());

    // ── CANDIDATE REGISTER ──
    rows.push(sectionHeader('Candidate Register'));
    rows.push(['#', 'Unique ID', 'Full Name', 'Chest No.', 'Batch ID', 'Current Stage', 'Status', 'Tests Passed', 'Tests Failed', 'Pipeline Journey', 'Last Activity Timestamp']);
    candidates.forEach((c, idx) => {
      const passCount = (c.test_results || []).filter(r => r.result === 'PASS').length;
      const failCount = (c.test_results || []).filter(r => r.result === 'FAIL').length;
      const lastLog = c.activity_log && c.activity_log.length > 0
        ? new Date(c.activity_log[c.activity_log.length - 1].timestamp).toLocaleString()
        : 'N/A';
      const journey = (c.activity_log || []).map(l => l.action).filter(Boolean).join(' > ') || 'N/A';
      rows.push([
        idx + 1,
        c.unique_id || '',
        `"${(c.full_name || '').replace(/"/g, "'")}"`,
        c.chest_number || 'N/A',
        c.batch_id || 'N/A',
        c.current_stage || '',
        c.status || '',
        passCount,
        failCount,
        `"${journey}"`,
        `"${lastLog}"`
      ]);
    });
    rows.push(blank());

    // ── AUDIT LOG ──
    rows.push(sectionHeader('Recent Audit Log (Last 50 Entries)'));
    rows.push(['#', 'Timestamp', 'Action', 'Log Type', 'Details', 'IP Address']);
    logs.slice(0, 50).forEach((log, idx) => {
      rows.push([
        idx + 1,
        `"${new Date(log.createdAt).toLocaleString()}"`,
        `"${log.action || ''}"`,
        `"${log.log_type || ''}"`,
        `"${(log.details || '').replace(/"/g, "'")}"`,
        `"${log.ip_address || 'N/A'}"`
      ]);
    });
    rows.push(blank());
    rows.push(['--- END OF REPORT ---']);
    rows.push([`ICFF Admin System | ${projectName} | Confidential — For Official Use Only`]);

    // UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectName.replace(/\s+/g, '_')}_Analytics_${now.toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // --- PRINT REPORT ---
  const printReport = () => {
    const now = new Date();
    const projectName = project?.name || 'All Projects';
    const totalCandidates = candidates.length;
    const rejected = candidates.filter(c => c.status === 'REJECTED').length;
    const completed = candidates.filter(c => c.status === 'COMPLETED').length;
    const active = totalCandidates - rejected - completed;
    let totalTests = 0, passedTests = 0;
    candidates.forEach(c => { (c.test_results || []).forEach(r => { totalTests++; if (r.result === 'PASS') passedTests++; }); });
    const passRate = totalTests > 0 ? Math.round((passedTests / totalTests) * 100) : 0;

    const stageRows = performanceStats.map(s => `
      <tr>
        <td>${s.name}</td>
        <td>${s.total}</td>
        <td style="color:#10b981;font-weight:800">${s.pass}%</td>
        <td style="color:#ef4444;font-weight:800">${s.fail}%</td>
        <td>
          <div style="width:120px;height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden">
            <div style="width:${s.pass}%;height:100%;background:#10b981;border-radius:4px"></div>
          </div>
        </td>
      </tr>`).join('');

    const candidateRows = candidates.slice(0, 100).map(c => {
      const passCount = (c.test_results || []).filter(r => r.result === 'PASS').length;
      const failCount = (c.test_results || []).filter(r => r.result === 'FAIL').length;
      const statusColor = c.status === 'REJECTED' ? '#ef4444' : c.status === 'COMPLETED' ? '#10b981' : '#3b82f6';
      return `<tr>
        <td>${c.unique_id || ''}</td>
        <td>${c.full_name || ''}</td>
        <td>${c.chest_number || 'N/A'}</td>
        <td>${c.current_stage || ''}</td>
        <td style="color:${statusColor};font-weight:800">${c.status || ''}</td>
        <td>${passCount}</td>
        <td>${failCount}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${projectName} — Analytics Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,700,1,0" rel="stylesheet" />
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; color: #0f172a; background: #fff; padding: 48px 56px; }
    .material-symbols-outlined { font-size: 18px; vertical-align: middle; line-height: 1; }

    /* Header */
    .report-header { display: flex; align-items: flex-start; gap: 20px; margin-bottom: 8px; }
    .header-icon { width: 52px; height: 52px; background: #800000; border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .header-icon .material-symbols-outlined { font-size: 28px; color: white; }
    h1 { font-size: 28px; font-weight: 900; color: #800000; line-height: 1.2; }
    .org-name { font-size: 12px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 4px; }
    .subtitle { font-size: 13px; color: #64748b; margin-top: 4px; }
    .divider { height: 2px; background: linear-gradient(90deg, #800000, transparent); margin: 24px 0 32px; border: none; }

    /* Summary cards */
    .report-meta { display: grid; grid-template-columns: repeat(6, 1fr); gap: 16px; margin-bottom: 40px; }
    .meta-block { background: #f8fafc; border-radius: 12px; padding: 16px 18px; border: 1px solid #e2e8f0; }
    .meta-icon { margin-bottom: 8px; }
    .meta-icon .material-symbols-outlined { font-size: 20px; color: #94a3b8; }
    .meta-label { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; font-weight: 700; margin-bottom: 4px; }
    .meta-value { font-size: 22px; font-weight: 900; color: #0f172a; }
    .meta-value.red { color: #ef4444; }
    .meta-value.green { color: #10b981; }
    .meta-value.blue { color: #3b82f6; }
    .meta-value.maroon { color: #800000; }

    /* Section headers */
    h2 { font-size: 15px; font-weight: 800; margin: 36px 0 14px; color: #800000; display: flex; align-items: center; gap: 10px; }
    h2 .material-symbols-outlined { font-size: 20px; }
    h2 .section-title { flex: 1; }
    h2::after { content: ''; flex: 1; height: 2px; background: #f1f5f9; display: block; }

    /* Tables */
    table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin-bottom: 8px; }
    th { text-align: left; padding: 10px 14px; background: #f8fafc; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; border-bottom: 2px solid #e2e8f0; font-weight: 700; }
    td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
    tr:nth-child(even) td { background: #fafafa; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
    .badge-pass { background: #dcfce7; color: #166534; }
    .badge-fail { background: #fee2e2; color: #991b1b; }
    .badge-active { background: #dbeafe; color: #1d4ed8; }
    .badge-completed { background: #d1fae5; color: #065f46; }
    .badge-rejected { background: #fee2e2; color: #991b1b; }
    .badge-pending { background: #f1f5f9; color: #64748b; }

    /* Footer */
    .footer { margin-top: 48px; padding-top: 20px; border-top: 2px solid #f1f5f9; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }
    .footer-logo { display: flex; align-items: center; gap: 8px; font-weight: 800; color: #800000; }
    .footer-logo .material-symbols-outlined { font-size: 16px; }

    @media print {
      body { padding: 20px 28px; }
      .report-meta { grid-template-columns: repeat(3, 1fr); }
      h2::after { content: none; }
    }
  </style>
</head>
<body>

  <!-- Report Header -->
  <div class="report-header">
    <div class="header-icon"><span class="material-symbols-outlined">analytics</span></div>
    <div>
      <div class="org-name">ICFF Admin System</div>
      <h1>${projectName}</h1>
      <div class="subtitle">Analytics &amp; Performance Report &nbsp;&bull;&nbsp; Generated on ${now.toLocaleString()}</div>
    </div>
  </div>
  <hr class="divider" />

  <!-- Summary Cards -->
  <div class="report-meta">
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">group</span></div>
      <div class="meta-label">Total Registered</div>
      <div class="meta-value">${totalCandidates}</div>
    </div>
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">sync</span></div>
      <div class="meta-label">Active in Pipeline</div>
      <div class="meta-value blue">${active}</div>
    </div>
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">block</span></div>
      <div class="meta-label">Eliminated</div>
      <div class="meta-value red">${rejected}</div>
    </div>
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">verified</span></div>
      <div class="meta-label">Completed</div>
      <div class="meta-value green">${completed}</div>
    </div>
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">percent</span></div>
      <div class="meta-label">Overall Pass Rate</div>
      <div class="meta-value green">${passRate}%</div>
    </div>
    <div class="meta-block">
      <div class="meta-icon"><span class="material-symbols-outlined">labs</span></div>
      <div class="meta-label">Tests Conducted</div>
      <div class="meta-value maroon">${totalTests}</div>
    </div>
  </div>

  <!-- Stage Performance -->
  <h2><span class="material-symbols-outlined">bar_chart</span> <span class="section-title">Stage-Wise Performance</span></h2>
  <table>
    <thead>
      <tr>
        <th>Stage Name</th>
        <th>Tests Conducted</th>
        <th>Passed</th>
        <th>Failed</th>
        <th>Pass Rate</th>
        <th>Visual</th>
      </tr>
    </thead>
    <tbody>
      ${performanceStats.length === 0
        ? '<tr><td colspan="6" style="color:#94a3b8;text-align:center;padding:24px">No stage test data recorded yet</td></tr>'
        : performanceStats.map(s => {
            const passed = Math.round((s.pass / 100) * s.total);
            const failed = s.total - passed;
            return `<tr>
              <td style="font-weight:700">${s.name}</td>
              <td>${s.total}</td>
              <td><span class="badge badge-pass">&#10003; ${passed}</span></td>
              <td><span class="badge badge-fail">&#10007; ${failed}</span></td>
              <td style="font-weight:800;color:${s.pass >= 70 ? '#10b981' : '#ef4444'}">${s.pass}%</td>
              <td>
                <div style="width:120px;height:8px;background:#f1f5f9;border-radius:4px;overflow:hidden;display:inline-block;vertical-align:middle">
                  <div style="width:${s.pass}%;height:100%;background:#10b981;border-radius:4px"></div>
                </div>
              </td>
            </tr>`;
          }).join('')
      }
    </tbody>
  </table>

  <!-- Candidate Register -->
  <h2><span class="material-symbols-outlined">badge</span> <span class="section-title">Candidate Register (Top 100)</span></h2>
  <table>
    <thead>
      <tr>
        <th>#</th><th>Unique ID</th><th>Full Name</th><th>Chest No.</th>
        <th>Current Stage</th><th>Status</th><th>Tests Passed</th><th>Tests Failed</th>
      </tr>
    </thead>
    <tbody>
      ${candidates.length === 0
        ? '<tr><td colspan="8" style="color:#94a3b8;text-align:center;padding:24px">No candidates registered</td></tr>'
        : candidates.slice(0, 100).map((c, i) => {
            const passCount = (c.test_results || []).filter(r => r.result === 'PASS').length;
            const failCount = (c.test_results || []).filter(r => r.result === 'FAIL').length;
            const badgeCls = c.status === 'REJECTED' ? 'badge-rejected' : c.status === 'COMPLETED' ? 'badge-completed' : c.status === 'IN_PROGRESS' ? 'badge-active' : 'badge-pending';
            return `<tr>
              <td style="color:#94a3b8;font-size:11px">${i + 1}</td>
              <td style="font-family:monospace;font-size:11px;font-weight:700">${c.unique_id || ''}</td>
              <td style="font-weight:600">${c.full_name || ''}</td>
              <td>${c.chest_number || 'N/A'}</td>
              <td>${c.current_stage || ''}</td>
              <td><span class="badge ${badgeCls}">${c.status || ''}</span></td>
              <td style="color:#10b981;font-weight:800">${passCount}</td>
              <td style="color:#ef4444;font-weight:800">${failCount}</td>
            </tr>`;
          }).join('')
      }
    </tbody>
  </table>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-logo">
      <span class="material-symbols-outlined">verified_user</span>
      ICFF Admin System
    </div>
    <div>${projectName} &nbsp;|&nbsp; Confidential &mdash; For Official Use Only &nbsp;|&nbsp; ${now.toLocaleDateString()}</div>
  </div>

  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  return (
    <div className="main-view animate-fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Reporting & Analytics</h2>
          <p style={{ color: 'var(--text-muted)' }}>
            Insights for <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{project?.name || 'All Projects'}</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn-secondary" onClick={printReport} style={{ padding: '10px 20px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <Icon icon="solar:printer-bold-duotone" width="18" /> Print Report
          </button>
          <button className="btn-primary" onClick={exportCSV} style={{ width: 'auto', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <Icon icon="solar:file-download-bold-duotone" width="18" /> Export CSV
          </button>
        </div>
      </div>

      {contextLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', background: 'white', borderRadius: '24px', marginBottom: '32px', border: '1px solid var(--border-light)' }}>
          <LoadingSpinner message="Synchronizing Real-time Analytics..." />
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '32px', marginBottom: '32px' }}>
        {/* Real Stage Performance */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: 800 }}>Stage Performance</h3>
          <div style={{ display: 'grid', gap: '20px' }}>
            {!project ? (
               <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                 Select a project to view metrics
               </div>
            ) : performanceStats.length === 0 ? (
               <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                 No stages defined for this project
               </div>
            ) : performanceStats.every(s => s.total === 0) ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                textAlign: 'center',
                padding: '24px',
                background: 'rgba(0,0,0,0.02)',
                borderRadius: '16px'
              }}>
                <Icon icon="solar:chart-2-bold-duotone" width="48" style={{ color: 'var(--primary)', opacity: 0.3, marginBottom: '12px' }} />
                <div style={{ fontWeight: 800, fontSize: '14px' }}>Waiting for Data</div>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>Begin processing candidates in the simulation to see live conversion rates.</p>
              </div>
            ) : (
              performanceStats.map(stat => (
                <PerformanceBar key={stat.name} label={stat.name} pass={stat.pass} fail={stat.fail} />
              ))
            )}
          </div>
        </div>

        {/* Real Candidate Journey */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: 800 }}>Candidate Journey</h3>
          <div style={{ position: 'relative', marginBottom: '24px' }}>
            <Icon icon="solar:magnifer-linear" style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search Chest # or Unique ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
              style={{ paddingLeft: '40px' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '300px', overflowY: 'auto', paddingRight: '8px' }}>
            {searchTerm ? (
              foundCandidate ? (
                <div
                  onClick={() => { setSelectedCandidate(foundCandidate); setIsDrawerOpen(true); }}
                  style={{ padding: '16px', borderRadius: '16px', background: 'rgba(128,0,0,0.02)', border: '1px solid var(--border-light)', cursor: 'pointer', transition: 'all 0.2s' }}
                  className="table-row-hover no-scale"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                      {foundCandidate.full_name?.charAt(0)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '15px' }}>{foundCandidate.full_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>CHEST: {foundCandidate.chest_number || 'N/A'} • {foundCandidate.unique_id}</div>
                    </div>
                  </div>
                  
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                    Current Stage: <span style={{ color: 'var(--primary)', fontWeight: 700 }}>{foundCandidate.current_stage}</span>
                  </div>

                  <button className="btn-primary" style={{ width: '100%', padding: '8px', fontSize: '12px' }}>
                    View Full History & Graph
                  </button>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '44px 24px', 
                  borderRadius: '24px',
                  background: 'rgba(239, 68, 68, 0.03)',
                  border: '1px dashed rgba(239, 68, 68, 0.2)'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '20px',
                    background: 'rgba(239, 68, 68, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px'
                  }}>
                    <Icon icon="solar:user-block-bold-duotone" width="32" color="#ef4444" />
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#b91c1c' }}>Not Found</div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                    ID "{searchTerm}" doesn't exist in <br/><strong>{project?.name || 'this drive'}</strong>.
                  </p>
                </div>
              )
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '44px 24px', 
                borderRadius: '24px',
                background: 'rgba(128, 0, 0, 0.03)',
                border: '1px dashed rgba(128, 0, 0, 0.15)'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '20px',
                  background: 'rgba(128, 0, 0, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px'
                }}>
                  <Icon icon="solar:magnifer-bold-duotone" width="32" color="var(--primary)" />
                </div>
                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--primary)' }}>Track Candidate</div>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  Enter a Chest Number to view <br/>live mission progress.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Audit Highlights */}
        <div className="glass-card">
          <h3 style={{ marginBottom: '24px', fontSize: '18px', fontWeight: 800 }}>Audit Highlights</h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ padding: '16px', background: 'rgba(128,0,0,0.03)', borderRadius: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Project Actions</div>
              <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--primary)', marginTop: '4px' }}>
                {project ? totalLogs : '---'}
              </div>
            </div>
            <div style={{ padding: '16px', background: 'rgba(128,0,0,0.02)', borderRadius: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Database Status</div>
              <div style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-main)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981' }}></div>
                SECURE
              </div>
            </div>
          </div>
          <button className="btn-secondary" style={{ width: '100%', marginTop: '24px' }}>
            Schedule PDF Report
          </button>
        </div>
      </div>
      </>
      )}

      <div className="glass-card" style={{ padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <h3 style={{ fontSize: '20px', fontWeight: 800 }}>
            {project ? `${project.name} Audit Trail` : 'Master Audit Trail'}
          </h3>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Icon icon="solar:user-linear" width="16" style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search User..."
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setPage(1); }}
                className="input-field"
                style={{ width: '180px', padding: '10px 10px 10px 36px' }}
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              className="input-field"
              style={{ width: '160px', padding: '8px 12px' }}
            >
              <option value="all">Every User Type</option>
              {roles.map(role => <option key={role} value={role}>{role.replace('_', ' ')}</option>)}
            </select>

            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="input-field"
              style={{ width: '160px', padding: '8px 12px' }}
            >
              <option value="all">Every log Type</option>
              <option value="ACTION">Action</option>
              <option value="SUSPICIOUS">Suspicious</option>
              <option value="SYSTEM">System Log</option>
            </select>
          </div>
        </div>

        <div style={{ minHeight: '520px', position: 'relative', background: '#fff', borderRadius: '16px', border: '1px solid var(--border-light)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8fafc', borderBottom: '2px solid var(--border-light)' }}>
                <th style={{ padding: '18px 16px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>TIMESTAMP</th>
                <th style={{ padding: '18px 16px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>USER & ROLE</th>
                <th style={{ padding: '18px 16px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>ACTION & DETAILS</th>
                <th style={{ padding: '18px 16px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>TYPE</th>
                <th style={{ padding: '18px 16px', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', textAlign: 'right' }}>IP ADDRESS</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '140px 0' }}>
                    <LoadingSpinner message="Fetching Audit Logs..." />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '100px 0', textAlign: 'center' }}>
                    <div className="animate-scale-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'rgba(128, 0, 0, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon icon="solar:database-line-duotone" width="40" color="var(--primary)" style={{ opacity: 0.5 }} />
                      </div>
                      <div>
                        <h4 style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text-main)', marginBottom: '4px' }}>No Data Available</h4>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', maxWidth: '280px', margin: '0 auto' }}>
                          We couldn't find any audit logs matching your current filters. Try adjusting your search criteria.
                        </p>
                      </div>
                      <button
                        onClick={() => { setUserSearch(''); setRoleFilter('all'); setTypeFilter('all'); }}
                        style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 700, fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Clear all filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log._id} className="table-row-hover" style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td style={{ padding: '16px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 700 }}>{log.user_name || 'SYSTEM'}</div>
                      <div style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: 800, opacity: 0.7 }}>{log.user_role || 'SYSTEM'}</div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '13px' }}>
                      <div style={{ fontWeight: 600 }}>{log.action}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{log.details}</div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 800,
                        padding: '4px 10px',
                        borderRadius: '20px',
                        background: log.type === 'SUSPICIOUS' ? '#fee2e2' : log.type === 'SYSTEM' ? '#f1f5f9' : '#e0f2fe',
                        color: log.type === 'SUSPICIOUS' ? '#991b1b' : log.type === 'SYSTEM' ? '#475569' : '#0369a1',
                        letterSpacing: '0.5px'
                      }}>
                        {log.type}
                      </span>
                    </td>
                    <td style={{ padding: '16px', fontSize: '12px', textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                      {log.ip_address || '---'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
            Showing page <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{page}</span> of <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{totalPages}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="btn-secondary"
              style={{ padding: '8px 16px', opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? 'not-allowed' : 'pointer' }}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                className={page === i + 1 ? "btn-primary" : "btn-secondary"}
                style={{ width: '40px', height: '40px', padding: 0 }}
              >
                {i + 1}
              </button>
            )).slice(Math.max(0, page - 3), Math.min(totalPages, page + 2))}
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="btn-secondary"
              style={{ padding: '8px 16px', opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <CandidateDetailDrawer
        candidate={selectedCandidate}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};

const PerformanceBar = ({ label, pass, fail }) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', fontWeight: 700 }}>
      <span>{label}</span>
      <span style={{ color: '#10b981' }}>{pass}% Pass</span>
    </div>
    <div style={{ height: '8px', display: 'flex', borderRadius: '4px', overflow: 'hidden', background: '#f1f5f9' }}>
      <div style={{ width: `${pass}%`, background: 'linear-gradient(90deg, #10b981, #34d399)' }}></div>
      <div style={{ width: `${fail}%`, background: '#fee2e2' }}></div>
    </div>
  </div>
);

export default Analytics;
